import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Gauge, RefreshCw, Loader2, Activity } from 'lucide-react';
import api from '../utils/axios';
import { MysqlSalud } from '../components/procesos/MysqlSalud';
import { ProcesoCard } from '../components/procesos/ProcesoCard';
import { CorridasTabla } from '../components/procesos/CorridasTabla';
import { VENTANAS } from '../components/procesos/procesosUtils';

const Total = ({ label, value, tone = 'text-slate-900', testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</p>
    <p className={`text-2xl font-extrabold tabular-nums ${tone}`} style={{ fontFamily: 'Manrope, sans-serif' }} data-testid={testid}>{value}</p>
  </div>
);

export default function ProcesosAutomaticos() {
  const [horas, setHoras] = useState(24);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [refresco, setRefresco] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData((await api.get('/procesos-automaticos/resumen', { params: { horas } })).data);
      setError(null);
    } catch (e) {
      setError(e.response?.status === 403 ? 'No tenés permiso para ver esta sección.' : 'No se pudieron cargar los procesos.');
    } finally { setLoading(false); }
  }, [horas]);

  useEffect(() => { load(); }, [load, refresco]);
  useEffect(() => { const t = setInterval(() => setRefresco((x) => x + 1), 60000); return () => clearInterval(t); }, []);

  const t = data?.totales;
  const selP = data?.procesos.find((p) => p.proceso === sel);

  return (
    <div className="vig-scope p-6 lg:p-8 space-y-6 bg-slate-50 min-h-full" data-testid="procesos-automaticos-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">Configuración</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Gauge className="h-9 w-9 text-teal-700" /> Procesos Automáticos
          </h1>
          <p className="text-sm text-slate-500 mt-1">Cada corrida automática del sistema: cuándo corrió, cuánto tardó, qué hizo y si falló, con el motivo. Se actualiza solo cada minuto.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/m/configuracion/actividad-usuarios" className="text-sm font-semibold text-teal-700 hover:text-teal-900 flex items-center gap-1.5 mr-2" data-testid="proc-link-actividad">
            <Activity size={15} /> Actividad de usuarios
          </Link>
          <div className="flex rounded-full bg-white border border-slate-200 p-1">
            {VENTANAS.map((v) => (
              <button key={v.v} onClick={() => setHoras(v.v)} data-testid={`proc-ventana-${v.v}`}
                className={`px-3.5 py-1 text-xs font-semibold rounded-full transition-colors duration-200 ${horas === v.v ? 'bg-teal-700 text-white' : 'text-slate-600 hover:text-slate-900'}`}>
                {v.l}
              </button>
            ))}
          </div>
          <Button variant="outline" onClick={() => setRefresco((x) => x + 1)} disabled={loading} data-testid="proc-actualizar-btn">
            <RefreshCw size={16} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
        </div>
      </div>

      {error && <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="proc-error">{error}</p>}
      {!error && <MysqlSalud refresco={refresco} />}
      {!data && loading && <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Total label="Corridas en el período" value={t.corridas} testid="proc-total-corridas" />
            <Total label="Corridas con error" value={t.errores} tone={t.errores ? 'text-rose-600' : 'text-slate-900'} testid="proc-total-errores" />
            <Total label="Procesos con la última en error" value={t.con_error} tone={t.con_error ? 'text-rose-600' : 'text-slate-900'} testid="proc-total-con-error" />
            <Total label="Procesos atrasados" value={t.atrasados} tone={t.atrasados ? 'text-amber-700' : 'text-slate-900'} testid="proc-total-atrasados" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="proc-cards">
            {data.procesos.map((p, i) => (
              <ProcesoCard key={p.proceso} p={p} index={i} activo={sel === p.proceso} onClick={() => setSel(sel === p.proceso ? null : p.proceso)} />
            ))}
            {!data.procesos.length && <p className="text-sm text-slate-400 col-span-full" data-testid="proc-sin-procesos">Todavía no hay corridas registradas.</p>}
          </div>
          <CorridasTabla horas={horas} proceso={sel} procesoNombre={selP?.nombre} onLimpiar={() => setSel(null)} refresco={refresco} />
        </>
      )}
    </div>
  );
}
